import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useAppStore } from '../../../store/appStore';
import type { Contact } from '../../../config/api';
import ContactDetail from '../components/ContactDetail';
import AIInsightsPanel from '../components/AIInsightsPanel';
import { ContactsIcon } from '../../../components/icons';
import { getFoundationContact, updateFoundationContact } from '../services/contactsFoundationApi';
import { toContactMutation, toLegacyContact } from '../services/contactViewModel';

// ─── AI Panel 3-state ──────────────────────────────────────────
export type AiPanelMode = 'collapsed' | 'peek' | 'full';

const AI_PANEL_WIDTHS: Record<AiPanelMode, number> = {
  collapsed: 0,
  peek: 320,
  full: 520,
};

const MODE_CYCLE: AiPanelMode[] = ['collapsed', 'peek', 'full'];

function nextMode(current: AiPanelMode): AiPanelMode {
  const idx = MODE_CYCLE.indexOf(current);
  return MODE_CYCLE[(idx + 1) % MODE_CYCLE.length];
}

const ContactDashboard: React.FC<{ onContactLoaded?: (contact: Contact | null) => void }> = ({ onContactLoaded }) => {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const { setSelectedContactId } = useAppStore();

  const [contact, setContact] = useState<Contact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [aiPanelMode, setAiPanelMode] = useState<AiPanelMode>('collapsed');

  // ─── Resize drag state ──────────────────────────────────────
  const [panelWidth, setPanelWidth] = useState(AI_PANEL_WIDTHS.collapsed);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  // Sync panelWidth when mode changes (non-drag)
  useEffect(() => {
    if (!isDragging.current) {
      setPanelWidth(AI_PANEL_WIDTHS[aiPanelMode]);
    }
  }, [aiPanelMode]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelWidth;

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      // Dragging left edge — moving left = wider panel
      const delta = dragStartX.current - ev.clientX;
      const newWidth = Math.max(200, Math.min(700, dragStartWidth.current + delta));
      setPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      // Snap to nearest mode
      setPanelWidth(prev => {
        if (prev < 160) { setAiPanelMode('collapsed'); return 0; }
        if (prev < 420) { setAiPanelMode('peek'); return 320; }
        setAiPanelMode('full'); return 520;
      });
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [panelWidth]);

  // ─── Data loading ───────────────────────────────────────────
  useEffect(() => {
    const loadContact = async () => {
      if (!contactId) {
        setError('No contact ID provided');
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        setError(null);
        setSelectedContactId(contactId);
        const foundContact = await getFoundationContact(keycloak?.token, contactId);
        if (foundContact?.id) {
          const legacyContact = toLegacyContact(foundContact);
          setContact(legacyContact);
          if (onContactLoaded) onContactLoaded(legacyContact);
        } else {
          setError('Contact not found');
          setContact(null);
          if (onContactLoaded) onContactLoaded(null);
        }
      } catch (err) {
        console.error('Failed to load contact:', err);
        setError('Failed to load contact details');
        setContact(null);
        if (onContactLoaded) onContactLoaded(null);
      } finally {
        setIsLoading(false);
      }
    };
    loadContact();
  }, [contactId, keycloak?.token, setSelectedContactId, onContactLoaded]);

  const handleContactUpdate = useCallback(async (updatedContact: Contact) => {
    try {
      const result = await updateFoundationContact(
        keycloak?.token,
        updatedContact.id,
        toContactMutation(updatedContact),
      );
      setContact(toLegacyContact(result));
    } catch (err) {
      console.error('Failed to update contact:', err);
    }
  }, [keycloak?.token]);

  const handleClose = useCallback(() => navigate('/contacts'), [navigate]);

  const handleToggleAiPanel = useCallback(() => {
    setAiPanelMode(prev => nextMode(prev));
  }, []);

  const handleSetAiMode = useCallback((mode: AiPanelMode) => {
    setAiPanelMode(mode);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lex-accent mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading contact information...</p>
        </div>
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <ContactsIcon />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-4 mb-2">Contact Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error || 'The contact you are looking for could not be found.'}
          </p>
          <button onClick={() => navigate('/contacts')} className="px-4 py-2 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded-lg font-medium transition-colors">
            Back to Contacts
          </button>
        </div>
      </div>
    );
  }

  const isOpen = aiPanelMode !== 'collapsed';
  const isPeek = aiPanelMode === 'peek';
  const currentWidth = isDragging.current ? panelWidth : AI_PANEL_WIDTHS[aiPanelMode];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <div className="flex-1 flex flex-row">
        {/* Main content — expands when AI panel is collapsed */}
        <div className="flex-1 min-w-0 transition-all duration-300">
          <ContactDetail
            contact={contact}
            onClose={handleClose}
            onUpdate={handleContactUpdate}
            aiPanelMode={aiPanelMode}
            onToggleAiPanel={handleToggleAiPanel}
            onSetAiMode={handleSetAiMode}
          />
        </div>

        {/* AI Insights Panel — 3-state sidebar */}
        {isOpen && (
          <div
            className="flex-shrink-0 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-all overflow-hidden relative"
            style={{
              width: currentWidth,
              transitionDuration: isDragging.current ? '0ms' : '300ms',
            }}
          >
            {/* Resize drag handle */}
            <div
              onMouseDown={handleMouseDown}
              className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize z-20 hover:bg-lex-accent/30 active:bg-lex-accent/40 transition-colors"
            />
            <div style={{ width: AI_PANEL_WIDTHS[aiPanelMode === 'peek' ? 'peek' : 'full'] }} className="h-full">
              <AIInsightsPanel
                contactId={contact.id}
                className="h-full"
                compact={isPeek}
              />
            </div>
          </div>
        )}

        {/* Collapsed AI rail — visible when panel is closed */}
        {!isOpen && (
          <button
            onClick={() => setAiPanelMode('peek')}
            className="flex-shrink-0 w-10 flex flex-col items-center justify-center gap-2 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            title="Open AI Insights"
          >
            <svg className="w-5 h-5 text-lex-accent" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
            </svg>
            <span className="text-[10px] font-medium text-lex-accent [writing-mode:vertical-lr] rotate-180">AI Insights</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ContactDashboard;
